import { useLocation } from "wouter";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users2, Filter, CalendarOff, Building2 } from "lucide-react";
import { memId } from "@/lib/display-id";
import { gstTreatmentLabel } from "@/lib/format";
// Phase 23-08: Member type removed (members table dropped). Use Account type.
import type { Account } from "@workspace/api-client-react";

interface MembersTableProps {
  members: Account[] | undefined;
  variant: "all" | "recurring" | "non-recurring";
  onViewMember?: (id: number) => void;
  isLoading?: boolean;
  hasActiveFilter?: boolean;
}

function subtypeDisplayLabel(s: string): string {
  return ({
    workstation: "Workstation",
    cabin: "Cabin",
    conference_room_hourly: "Conference Room",
    meeting_room_hourly: "Meeting Room",
    day_pass: "Day Pass",
    event_hourly: "Event",
    shooting_hourly: "Shooting",
  } as Record<string, string>)[s] ?? s;
}

function SubtypeChip({ subtype }: { subtype: string }) {
  return (
    <Badge variant="outline" className="text-xs font-normal">
      {subtypeDisplayLabel(subtype)}
    </Badge>
  );
}

function CategoryBadge({ category }: { category: string }) {
  if (category === "Recurring") {
    return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Recurring</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Non-recurring</Badge>;
}

function EmptyState({ variant, hasActiveFilter }: { variant: "all" | "recurring" | "non-recurring"; hasActiveFilter?: boolean }) {
  if (hasActiveFilter) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Filter className="h-8 w-8 text-muted-foreground mb-3" />
        <div className="text-base font-semibold">No members match this filter</div>
        <div className="text-sm text-muted-foreground mt-1">Try clearing the subtype filter or switching tabs.</div>
      </div>
    );
  }
  if (variant === "recurring") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Building2 className="h-8 w-8 text-muted-foreground mb-3" />
        <div className="text-base font-semibold">No recurring members</div>
        <div className="text-sm text-muted-foreground mt-1">Workstation and cabin members appear here after lead conversion.</div>
      </div>
    );
  }
  if (variant === "non-recurring") {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CalendarOff className="h-8 w-8 text-muted-foreground mb-3" />
        <div className="text-base font-semibold">No non-recurring members</div>
        <div className="text-sm text-muted-foreground mt-1">Day pass, conference room, event, and shooting members appear here after Walk-in or lead conversion.</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Users2 className="h-8 w-8 text-muted-foreground mb-3" />
      <div className="text-base font-semibold">No members yet</div>
      <div className="text-sm text-muted-foreground mt-1">Convert a lead or use Walk-in to add the first member.</div>
    </div>
  );
}

export function MembersTable({ members, variant, onViewMember, isLoading, hasActiveFilter }: MembersTableProps) {
  const [, navigate] = useLocation();

  const handleViewMember = (id: number) => {
    if (onViewMember) {
      onViewMember(id);
    } else {
      // This table is reachable only from the legacy /members-legacy list and
      // deals in accountIds, not the new contact-keyed /members/:id detail.
      navigate(`/members-legacy/${id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!members || members.length === 0) {
    return <EmptyState variant={variant} hasActiveFilter={hasActiveFilter} />;
  }

  if (variant === "all") {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-28">Mobile</TableHead>
            <TableHead className="w-24">Category</TableHead>
            <TableHead className="w-28">Subtype</TableHead>
            <TableHead>Plan / Last Booking</TableHead>
            <TableHead className="w-24">Cycle Mode</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((m) => (
            <TableRow key={m.id}>
              <TableCell className="font-mono text-xs">{memId(m.id)}</TableCell>
              <TableCell className="font-medium">{m.legalName}</TableCell>
              <TableCell className="font-mono text-xs">{m.billingPhone ?? "—"}</TableCell>
              {/* Phase 10 (Plan 10-07): memberCategory + memberSubtype columns dropped — show planId only. */}
              <TableCell className="text-xs text-muted-foreground">—</TableCell>
              <TableCell className="text-xs text-muted-foreground">—</TableCell>
              <TableCell className="text-sm">{m.kind}</TableCell>
              <TableCell className="text-xs">{gstTreatmentLabel(m.gstTreatment)}</TableCell>
              <TableCell>
                <Button size="sm" variant="ghost" onClick={() => handleViewMember(m.id)}>
                  View Member
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (variant === "recurring") {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-20">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-28">Subtype</TableHead>
            <TableHead className="w-28">Plan</TableHead>
            <TableHead className="w-24">Cycle Mode</TableHead>
            <TableHead className="w-20">Billing Day</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((m) => (
            <TableRow key={m.id}>
              <TableCell className="font-mono text-xs">{memId(m.id)}</TableCell>
              <TableCell className="font-medium">{m.legalName}</TableCell>
              {/* Phase 10 (Plan 10-07): memberSubtype dropped — placeholder cell. */}
              <TableCell className="text-xs text-muted-foreground">—</TableCell>
              <TableCell className="text-sm">{m.kind}</TableCell>
              <TableCell className="text-xs">{gstTreatmentLabel(m.gstTreatment)}</TableCell>
              <TableCell className="text-sm">{m.placeOfSupply}</TableCell>
              <TableCell>
                <Button size="sm" variant="ghost" onClick={() => handleViewMember(m.id)}>
                  View Member
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // non-recurring variant
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-20">ID</TableHead>
          <TableHead>Name</TableHead>
          <TableHead className="w-28">Subtype</TableHead>
          <TableHead className="w-28">Last Booking</TableHead>
          <TableHead className="w-24 text-right">Total Bookings</TableHead>
          <TableHead className="w-28">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((m) => (
          <TableRow key={m.id}>
            <TableCell className="font-mono text-xs">{memId(m.id)}</TableCell>
            <TableCell className="font-medium">{m.legalName}</TableCell>
            <TableCell className="text-xs text-muted-foreground">—</TableCell>
            <TableCell className="text-xs text-muted-foreground">None</TableCell>
            <TableCell className="text-right text-sm">—</TableCell>
            <TableCell>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => handleViewMember(m.id)}>
                  View Member
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigate(`/booking/new?memberId=${m.id}`)}>
                  New Booking
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
